import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileStickyBar from "@/components/MobileStickyBar";
import Home from "@/pages/Home";
import FindCare from "@/pages/FindCare";
import HowItWorks from "@/pages/HowItWorks";
import CareOptions from "@/pages/CareOptions";
import BoardAndCare from "@/pages/BoardAndCare";
import ForProfessionals from "@/pages/ForProfessionals";
import About from "@/pages/About";
import Resources from "@/pages/Resources";
import FamilyExperiences from "@/pages/FamilyExperiences";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import GuidePage from "@/pages/GuidePage";
import "@/App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error("CareCraft app error:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-warmwhite px-6 text-center">
          <p className="font-serif text-3xl font-medium text-forest">
            Something went wrong on our end.
          </p>
          <p className="mt-3 text-charcoal/70">
            Please try again, or call us at (818) 590-1533.
          </p>
          <a
            href="/"
            className="mt-8 inline-flex min-h-[48px] items-center rounded-full bg-forest px-7 py-3 font-medium text-ivory"
          >
            Back to the homepage
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function NotFound() {
  return (
    <section className="bg-warmwhite pt-[72px]">
      <div className="mx-auto max-w-site px-4 py-24 sm:px-6 lg:px-12">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-clay">Page not found</p>
        <h1 className="mt-6 font-serif text-4xl font-medium text-forest sm:text-5xl">
          That page doesn't exist, but the next step is still simple.
        </h1>
        <p className="mt-5 max-w-xl text-lg text-charcoal/75">
          Start by telling us what's happening. We'll listen first, then help you understand what
          options may make sense for your family.
        </p>
        <div className="mt-9 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="notfound-cta-talk-with-doris"
            className="inline-flex min-h-[48px] items-center rounded-full bg-forest px-7 py-3 font-medium text-ivory transition-colors hover:bg-forest-dark"
          >
            Talk With Doris
          </Link>
          <Link
            to="/"
            data-testid="notfound-link-home"
            className="inline-flex min-h-[48px] items-center rounded-full border border-forest/30 px-7 py-3 font-medium text-forest transition-colors hover:bg-forest/5"
          >
            Back to the homepage
          </Link>
        </div>
      </div>
    </section>
  );
}

function Shell() {
  return (
    <div className="flex min-h-screen flex-col">
      <ScrollManager />
      <Header />
      <main className="flex-1 pb-[76px] md:pb-0">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/find-care" element={<FindCare />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/care-options" element={<CareOptions />} />
          <Route path="/board-and-care" element={<BoardAndCare />} />
          <Route path="/for-professionals" element={<ForProfessionals />} />
          <Route path="/about" element={<About />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/family-experiences" element={<FamilyExperiences />} />
          <Route path="/resources/:slug" element={<GuidePage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <MobileStickyBar />
      <Toaster position="top-center" />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const lenis = new Lenis({ lerp: 0.11 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
